import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { isSameData } from '../../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { LocalizedString } from '../localized-string.js';
import { BusinessLevelMarketingSettings } from './business-level-marketing-settings.js';

/**
 * Publicly available subset of {@link BusinessLevelMarketingSettings}.
 */
export class BusinessLevelMarketingInfo extends DataModel {
  static readonly TYPE_ID: string = 'c70e521a71234701b6f840e7e0527a25';

  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly isSubscribingEnabled: boolean,
    public readonly isSubscribingRequired: boolean,
    public readonly consentText: LocalizedString | null,
  ) {
    super();
  }

  get $typeId(): string {
    return BusinessLevelMarketingInfo.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, BusinessLevelMarketingInfo.SCHEMA_VERSION);
  }

  static create(args: {
    isSubscribingEnabled: boolean,
    isSubscribingRequired: boolean,
    consentText: LocalizedString | null,
  }): BusinessLevelMarketingInfo {
    return new BusinessLevelMarketingInfo(
        args.isSubscribingEnabled,
        args.isSubscribingRequired,
        args.consentText,
    );
  }

  static fromBusinessLevelMarketingSettings(settings: BusinessLevelMarketingSettings): BusinessLevelMarketingInfo {
    return BusinessLevelMarketingInfo.create({
      isSubscribingEnabled: settings.isSubscribingEnabled,
      isSubscribingRequired: settings.isSubscribingRequired,
      consentText: settings.consentText,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessLevelMarketingInfo {
    return BusinessLevelMarketingInfo.create({
      isSubscribingEnabled: MapUtils.getBoolean(map, 'isSubscribingEnabled'),
      isSubscribingRequired: MapUtils.getBoolean(map, 'isSubscribingRequired'),
      consentText: context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'consentText')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      isSubscribingEnabled: this.isSubscribingEnabled,
      isSubscribingRequired: this.isSubscribingRequired,
      consentText: context.toDataOrNull(this.consentText),
    };
  }

  clone(): this {
    return BusinessLevelMarketingInfo.create({
      isSubscribingEnabled: this.isSubscribingEnabled,
      isSubscribingRequired: this.isSubscribingRequired,
      consentText: this.consentText,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.isSubscribingEnabled === this.isSubscribingEnabled &&
        other.isSubscribingRequired === this.isSubscribingRequired &&
        isSameData(other.consentText, this.consentText);
  }
}

export class BusinessLevelMarketingInfoBuilder extends DataModelBuilder<BusinessLevelMarketingInfo> {
  build(context: SerializationContext, map: SerializedData): BusinessLevelMarketingInfo {
    return BusinessLevelMarketingInfo.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BusinessLevelMarketingInfo.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessLevelMarketingInfo.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /** Version 2:
     * - Add 'isSubscribingRequired' field of type {@link boolean}.
     */
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'isSubscribingRequired', false);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
